import { api } from '@api'
import { useChannel } from '@features/chat/state'
import type { MessageMention, MessageMeta, Notification } from '@hvf/contracts'
import { type InfiniteData, useQueryClient } from '@tanstack/solid-query'
import { createEffect, onCleanup } from 'solid-js'
import { useService } from 'solid-services'
import { userId } from './auth'
import { useChannels } from './channels/channels'
import { useMessageMention } from './mentions/useMentions'
import { useNotifications } from './notifications'

export function useSessionRealtime() {
  const queryClient = useQueryClient()
  const { setNewNotifications } = useService(useNotifications)()
  const { setMessageMentions } = useService(useMessageMention)()

  const { channelsResource, mutateChannels } = useService(useChannels)()
  const { activeChannelId } = useService(useChannel)()

  createEffect(() => {
    const currentUserId = userId()

    if (!currentUserId) return

    console.log('Initializing global session realtime for:', currentUserId)

    const sessionChannel = api().channel(`session:${currentUserId}`)

    // 2. Attach Notification Listener
    sessionChannel.on<Notification>(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'notification',
        filter: `user_id=eq.${currentUserId}`,
      },
      (payload) => {
        setNewNotifications(true)

        // FIX: Let's inspect the actual shape of the Infinite Query cache
        queryClient.setQueryData<InfiniteData<any>>(
          ['notifications', currentUserId],
          (old) => {
            console.log('--- DEBUG: NOTIFICATION REALTIME PAYLOAD ---')
            console.log('old:', old)
            console.log('old.pages:', old?.pages)
            if (old?.pages && old.pages.length > 0) {
              console.log('old.pages[0]:', old.pages[0])
            }
            console.log('--------------------------------------------')

            // Returning `old` for now so it doesn't crash while we inspect the logs
            return old
          },
        )

        queryClient.invalidateQueries({
          queryKey: ['notifications', 'unread', currentUserId]
        })
      },
    )

    // 3. Attach Mention Listener
    sessionChannel.on<MessageMention>(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'message_mention',
        filter: `user_id=eq.${currentUserId}`,
      },
      (payload) => {
        setMessageMentions((prev) => [...prev, payload.new])
      },
    )

    // 4. Attach Message Meta Listener
    sessionChannel.on<MessageMeta>(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'message_meta',
      },
      (payload) => {
        const updated = payload.new

        mutateChannels((prevChannels) => {
          if (!prevChannels) return prevChannels

          const channelIndex = prevChannels.findIndex(
            (c) => c.id === updated.channel_id
          )

          if (channelIndex === -1) return prevChannels

          const newChannels = [...prevChannels]
          const channel = newChannels[channelIndex]

          const isCurrentlyViewing = activeChannelId() === channel.id
          const currentCount = Number(channel.unread_count || 0)

          newChannels[channelIndex] = {
            ...channel,
            last_message_at: updated.last_message_at,
            last_message_id: updated.last_message_id,
            unread_count: isCurrentlyViewing ? 0 : currentCount + 1,
          }

          return newChannels
        })
      },
    )

    sessionChannel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Global session realtime connected!')
      }
    })

    onCleanup(() => {
      console.log('Tearing down global session realtime')
      api().removeChannel(sessionChannel)
    })
  })

  return {}
}
